<template>
  <div class="live-auction-panel">
    <div class="title-wrapper">
      <h3 class="text-h6 font-weight-bold">Live Auctions</h3>
      <v-btn icon @click="closePanel()" class="close-btn">
        <v-icon large>mdi-close</v-icon>
      </v-btn>
    </div>
    <transition-group
      name="fade-up"
      tag="div"
      class="auction-card-wrapper"
    >
      <v-card
        v-for="(auction, index) in live_auction_previews"
        :key="auction.id"
        class="auction-card"
        elevation="3"
      >
        <div class="card-inner">
          <v-simple-table dense>
            <tbody>
              <tr v-for="i in Math.ceil(summaryRows(auction).length / 2)" :key="'summary-row-' + i">
                <td v-for="j in 2" :key="'summary-cell-' + j">
                  <div v-if="summaryRows(auction)[(i - 1) * 2 + (j - 1)]">
                    <span class="label">
                      {{ summaryRows(auction)[(i - 1) * 2 + (j - 1)].label }}:
                    </span>
                    <span class="value">
                      <template v-if="row = summaryRows(auction)[(i - 1) * 2 + (j - 1)]">
                        <v-icon
                          small
                          v-if="row.label === 'Documents'"
                          :color="row.value ? 'green' : 'grey'"
                          class="ml-1"
                        >
                          mdi-file-document
                        </v-icon>
                        {{ row.label !== 'Documents' ? row.value : '' }}
                      </template>
                    </span>
                  </div>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </div>
        <v-divider class="my-2" />
        <div class="bid-section">
          <v-row class="bottom-card-row" no-gutters>
            <v-col cols="6"><strong>Buy Now Fee:</strong> ${{ auction.sql_minimum_bid }}</v-col>
            <v-col cols="6"><strong>Time Left:</strong> {{ getTimeLeft(auction.expired_time) }}</v-col>
          </v-row>
          <v-row class="bottom-card-row" no-gutters v-if="leadAllocationId == auction.id">
            <v-col cols="12">
              <label for="leadAllocation">Assign Lead To Broker</label>
              <v-autocomplete v-model="leadAllocation" id="leadAllocation" :items="podUserOptions" dense></v-autocomplete>
            </v-col>
          </v-row>
          <v-row class="bottom-card-row" no-gutters>
            <v-col cols="12">
              <v-btn 
                :disabled="getTimeLeft(auction.expired_time) === 'Expired!' || !auction.has_current_veda_report"
                class="btn" 
                @click="viewTrafficLights(auction)"
              >
                <span>View Traffic Lights</span>
              </v-btn>
            </v-col>
          </v-row>
          <v-row class="bottom-card-row" no-gutters>
            <v-col cols="12">
              <v-btn 
                v-if="leadAllocationId != auction.id"
                class="btn" 
                :disabled="activeBuyId === auction.id || getTimeLeft(auction.expired_time) === 'Expired!'"
                @click="submitBuyNow(auction)"
              >
                <span>{{ activeBuyId === auction.id ? 'PROCESSING' : 'BUY NOW' }}</span>
                <v-progress-circular
                  v-if="activeBuyId === auction.id"
                  indeterminate
                  color="green"
                  size="20"
                  class="loading-icon"
                />
              </v-btn>
              <v-btn 
                v-else
                :disabled="buying"
                class="btn" 
                @click="submitLeadAllocation()"
              >
                <span>{{ buying ? 'PROCESSING' : 'SUBMIT' }}</span>
                <v-progress-circular
                  v-if="buying"
                  indeterminate
                  color="green"
                  size="20"
                  class="loading-icon"
                />
              </v-btn>
            </v-col>
          </v-row>
        </div>
      </v-card>
    </transition-group>
    <v-row class="see-all-row">
      <v-btn
        class="see-all-btn"
        @click="goToAllAuctions()"
      >
        See All Auctions
      </v-btn>
    </v-row>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import AlertDialog from "@/components/Dialogs/AlertDialog";
import DOMPurify from 'dompurify';

export default {
  name: "LiveAuctionPanel",
  components: {
    AlertDialog
  },
  props: {
    visible: Boolean
  },
  watch: {
    visible(val) {
      if (val) {
        this.clearTimer();
        this.$store.dispatch("fetch_live_auction_previews");
        this.startTimer();
      } else {
        this.clearTimer();
      }
    },
  },
  computed: {
    ...mapGetters(["live_auction_previews", "show_live_auction_drawer"]),
    isPodOwner() {
      let userPods = this.$store.state.user.user_pods;

      let ownedPods = userPods.filter((item) => {
        return item.is_pod_owner;
      });

      return ownedPods.length;
    },
    podUserOptions() {
      let data = [];
      const userPods = this.$store.state.user.user_pods;

      userPods && Array.isArray(userPods) && userPods.forEach(pod => {
        if (pod.is_pod_owner) {
          data.push({
            header: pod.name
          });

          pod.users && Array.isArray(pod.users) && pod.users.forEach(user => {
            data.push({
              text: `${user.first_name} ${user.last_name}${this.$store.state.user.id === user.id ? ' (You)' : ''}`,
              value: user.id,
              disabled: !user.same_tier
            });

          });
        }
      });
     
      return data;
    }
  },
  data() {
    return {
      currentTime: Date.now(),
      timeLeftTimer: null,
      activeBuyId: null,
      leadAllocationId: null,
      leadAllocation: this.$store.state.user.id,
      requestPayload: null,
      needAllocation: false,
      buying: false
    };
  },
  mounted() {
    if (this.visible) {
      this.clearTimer();
      this.$store.dispatch("fetch_live_auction_previews");
      this.startTimer();
    }
  },
  beforeDestroy() {
    this.clearTimer();
  },
  methods: {
    goToAllAuctions() {
      this.$router.push(`/application/new/tools/sql-auction`);
    },
    closePanel() {
      this.$store.commit("set_show_live_auction_drawer", false);
      this.clearTimer();
    },
    summaryRows(auction) {
      return [
        { label: "Grading Score", value: auction.grading_score ?? "—" },
        { label: "Loan Type", value: auction.loan_type ?? "—" },
        { label: "Asset Type", value: auction.asset_type ?? "—" },
        { label: "Employment", value: auction.employment_status ?? "—" },
        { label: "Id", value: auction.id ?? "—" },
        { label: "Postcode", value: auction.post_code ?? "—" },
        { label: "Visa", value: auction.visa_status ?? "—" },
        { label: "Residency", value: auction.address_ownership ?? "—" },
        // { label: "Age of Lead", value: this.getLeadAge(auction.loan_application_created_at) },
        { label: "NAF", value: auction.naf ?? "—" },
        {
          label: "Documents",
          icon: "mdi-file-document",
          iconCondition: !!auction.documents,
          value: auction.documents,
        },
      ];
    },
    getLeadAge(createdAt) {
      if (!createdAt) return "—";
      const now = new Date();
      const created = new Date(createdAt);
      const diffDays = Math.floor((now - created) / (1000 * 60 * 60 * 24));
      return `${diffDays} days`;
    },
    startTimer() {
      if (this.timeLeftTimer) return; // Don't double-run
      this.timeLeftTimer = setInterval(() => {
        this.currentTime = Date.now();
      }, 1000);
    },
    clearTimer() {
      if (this.timeLeftTimer) {
        clearInterval(this.timeLeftTimer);
        this.timeLeftTimer = null;
      }
    },
    getTimeLeft(expired_time) {
      if (expired_time) {
        let timeLeft = expired_time - this.currentTime;
        //If time left is < 0 or the auction is no longer active
        if (timeLeft <= 0) {
          return "Expired!";
        }
        return `${(Math.floor(timeLeft/60000)).toFixed()} m ${((timeLeft/1000)%60).toFixed()} s `;
      }
      return '';
    },
    async submitBuyNow(auction) {
      if (this.getTimeLeft(auction.expired_time) === "Expired!") {
        this.$modal.show(AlertDialog, {
          title: "Error",
          message: "Failed to process buy for selected auction. Auction expired!",
        });
        return;
      }
      this.activeBuyId = auction.id;
      const amount = auction.sql_minimum_bid;
      const auctionId = auction.id;
      await this.submitBidToZink(auctionId, amount);
      this.activeBuyId = null;
    },
    async viewTrafficLights(auction) {
      try {
        await this.$store.dispatch("fetch_veda_report", {
          loan_application_id: auction.loan_application_id,
          is_auction_veda: 1
        });
        this.$store.commit("set_veda_summary_dialog", auction.loan_application_id);
      } catch(error) {
        console.log(error);
      }
    },
    async submitBidToZink(auctionId, amount) {
      try {
        const response = await this.$store.dispatch('submit_bid', {
            auction_id: auctionId,
            bid_amount: amount,
            is_max_bid: false,
            is_buy_now: true,
            is_new: 'Yes',
            is_pod_owner: this.isPodOwner
          });

        if (this.isPodOwner && response.message === "Lead allocation required.") {
          this.requestPayload = {
            auction_id: auctionId,
            bid_amount: amount,
            is_max_bid: false,
            is_buy_now: true,
            is_new: 'Yes',
          };
          this.leadAllocation = this.$store.state.user.id;
          this.leadAllocationId = auctionId;
        } else {
          let message = response.message;
        
          if (response.link) {
            message += `<br/> Please click this arrow to view the application <a href="${response.link}" target="_blank">`;
            message += `<i class="md-icon md-icon-font md-theme-default" style="font-weight: 900; color: blue;">
                          arrow_forward
                        </i></a>`;
            message = DOMPurify.sanitize(message);
          }

          this.$modal.show(AlertDialog, {
            title: response.title,
            message: message,
            isHtml: response.link ? true : false
          });
        
          await this.$store.dispatch('fetch_live_auction_previews');
        }

      } catch (err) {
        console.error('Error buying auction from Live Auction Panel', err);
      }
    },
    async submitLeadAllocation() {
      this.buying = true;
      try {
        this.requestPayload["user_id"] = this.leadAllocation;

        const response = await this.$store.dispatch('submit_bid', this.requestPayload);

        if (response.message === "Purchase successful.") {
          this.leadAllocation = this.$store.state.user.id;
          this.leadAllocationId = null;
        }

        this.$modal.show(AlertDialog, {
          title: response.title,
          message: response.message,
        });
        await this.$store.dispatch('fetch_live_auction_previews');
      } catch (err) {
        console.log(err);
      } finally {
        this.buying = false;
      }
    },
  }
};
</script>
<style lang="scss" scoped>
@import "@/assets/scss/theme.scss";

.see-all-row {
  align-items: center;
  justify-content: center;
  margin-top: 20px;
  margin-bottom: 20px;
}

.see-all-btn {
  font-weight: bold;
  text-transform: uppercase;
  justify-content: center;
  color: white !important;
  width: 90%;
  background-color: $ausloans-orange !important;
}

.title-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding: 0px 4px;
}

.close-btn {
  margin-left: auto;
  margin-right: -8px;
}

.live-auction-panel {
  padding: 20px;
}
.auction-card {
  border-radius: 10px !important;
  margin-bottom: 20px;
}

.label {
  font-weight: 500;
  color: #555;
  font-size: 17px;
}

.value {
  margin-left: 4px;
  color: #222;
  font-size: 16px !important;
}

.bottom-card-row {
  padding: 0 16px;
}

.show-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9rem;
  color: #444;
  cursor: pointer;
  padding: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.card-inner {
  padding: 16px 16px 0px 16px;
}

.bid-section {
  font-size: 1.1rem;
  line-height: 1.6;
  padding: 20px;
  background-color: #fafafa;
  border-top: 1px solid #eee;
  border-radius: 0 0 12px 12px;
}

.bid-section strong {
  font-size: 1.2rem;
  color: #1e1e1e;
}

.btn {
  position: relative;
  margin-top: 10px !important;
  font-weight: bold;
  text-transform: uppercase;
  background-color: $ausloans-blue !important;
  color: white !important;
  width: 100% !important;
}

.btn-modal {
  font-weight: bold;
  text-transform: uppercase;
  background-color: $ausloans-blue !important;
  color: white !important;
}

.loading-icon {
  margin-left: 16px;
  color: $ausloans-green !important;
}

.fade-up-enter-active,
.fade-up-leave-active {
  transition: all 1s ease;
}

.fade-up-enter-from {
  opacity: 0;
  transform: translateY(10px);
}
.fade-up-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
